import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { mediaQueries, fonts, palette } from '../../styles/variables'
import { getOS } from '../../utils/functions'
import { Text } from '../topography'

import InvalidTooltip from './InvalidTooltip'

const FieldInputWrapper = styled.div((props) => ({
  background: 'none',
  position: 'relative',
  ...(props.styles || {}),
}))

const FieldInputRightElement = styled.div({
  position: 'absolute',
  top: '20px',
  right: '20px',
  [mediaQueries.mobile]: {
    top: '17px',
  },
})

export const FieldInput = styled.input(
  ({
    width = '100%',
    height = 50,
    backgroundColor = palette.white,
    marginBottom = '10px',
    hasError = false,
    style = {},
  }) => ({
    height,
    width,
    padding: '10px 20px',
    fontSize: '15px',
    marginBottom,
    fontFamily: fonts.primaryLight,
    backgroundColor: `${backgroundColor} !important`,
    outline: 'none',
    borderRadius: '2px',
    WebkitAppearance: 'none',
    border: `1px solid ${hasError ? palette.danger : palette.lightGray}`,
    '::placeholder': {
      color: palette.lightGray,
      fontWeight: getOS() === 'Mac' ? 900 : '400',
    },
    ...style,
  }),
)

const FieldInputDecorator = ({
  value,
  onChange,
  styles,
  inputRef,
  testId = '',
  rightElemStyle,
  ...rest
}) => {
  if (rest.rightElement) {
    return (
      <FieldInputWrapper styles={styles}>
        <FieldInput
          data-testid={testId}
          value={value}
          onChange={onChange}
          ref={inputRef}
          {...rest}
        />
        <FieldInputRightElement style={rightElemStyle}>
          {rest.rightElement()}
        </FieldInputRightElement>
      </FieldInputWrapper>
    )
  }
  return <FieldInput {...rest} />
}

export const UberInput = styled.input(
  ({
    width = '100%',
    height = 40,
    backgroundColor = palette.white,
    borderColor = palette.lightGray,
    placeholderColor = palette.gray,
    focusedBorderColor,
  }) => ({
    height,
    width,
    padding: '10px 16px',
    fontSize: '14px',
    lineHeight: '16px',
    fontFamily: fonts.primary,
    backgroundColor: `${backgroundColor} !important`,
    outline: 'none',
    borderRadius: '2px',
    WebkitAppearance: 'none',
    border: `1px solid ${borderColor}`,
    '::placeholder': {
      color: placeholderColor,
      fontWeight: getOS() === 'Mac' ? 900 : '400',
    },
    ':focus': {
      borderColor: focusedBorderColor || borderColor,
    },
  }),
)

export const UberInputField = ({
  value,
  onChange,
  styles,
  inputRef,
  invalidMessage,
  rightElement,
  ...rest
}) => {
  if (rightElement) {
    return (
      <FieldInputWrapper styles={styles}>
        <UberInput
          value={value}
          onChange={onChange}
          ref={inputRef}
          borderColor={invalidMessage ? palette.danger : palette.lightGray}
          style={{
            paddingRight: rightElement ? '35px' : '16px',
          }}
          {...rest}
        />
        <FieldInputRightElement style={{ top: 10, right: 12 }}>
          {rightElement()}
        </FieldInputRightElement>
        {invalidMessage && (
          <Text
            fontSize="12px"
            lineHeight="12px"
            color={palette.danger}
            margin="8px 0 0 0"
          >
            {invalidMessage}
          </Text>
        )}
      </FieldInputWrapper>
    )
  }
  return (
    <FieldInputWrapper styles={styles}>
      <UberInput
        value={value}
        onChange={onChange}
        ref={inputRef}
        borderColor={invalidMessage ? palette.danger : palette.lightGray}
        style={{
          paddingRight: rightElement ? '35px' : '16px',
        }}
        {...rest}
      />
      {invalidMessage && (
        <Text
          fontSize="12px"
          lineHeight="12px"
          color={palette.danger}
          margin="8px 0 0 0"
        >
          {invalidMessage}
        </Text>
      )}
    </FieldInputWrapper>
  )
}

const RightElement = ({
  rightElement,
  isValid,
  forceDisplayRightElement,
  testId,
  invalidMessage,
}) => (
  <div data-testid={testId} style={{ display: 'flex', marginTop: '-3px' }}>
    {(isValid || forceDisplayRightElement) && rightElement && rightElement()}
    <InvalidTooltip show={!isValid} message={invalidMessage} />
  </div>
)

export default forwardRef(
  (
    {
      placeholder,
      name,
      value,
      onChange,
      isValid,
      invalidMessage,
      rightElement,
      styles,
      backgroundColor,
      inputRef,
      forceDisplayRightElement = false,
      testId = '',
      ...props
    },
    ref,
  ) => (
    <FieldInputDecorator
      placeholder={placeholder}
      name={name}
      value={value}
      onChange={onChange}
      styles={styles}
      backgroundColor={backgroundColor}
      rightElement={() => (
        <RightElement
          rightElement={rightElement}
          isValid={isValid}
          forceDisplayRightElement={forceDisplayRightElement}
          testId={testId}
          invalidMessage={invalidMessage}
        />
      )}
      inputRef={inputRef || ref}
      hasError={!isValid}
      {...props}
    />
  ),
)
